'use client';

import { FC, useEffect, useRef, useState } from 'react';

import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import { getMedia } from '@/lib/utils';

import css from './style.module.scss';
import { YesQCanDto } from './types';

interface Props {
	data: YesQCanDto;
}

const YesQCan: FC<Props> = ({ data }) => {
	const { images, title, items: itemsList } = data;

	const device = useDevice();

	const wrapperRef = useRef<HTMLDivElement>(null);
	const itemsRef = useRef<HTMLLIElement[]>([]);
	const slidesRef = useRef<HTMLDivElement[]>([]);
	const [loadedIndexes, setLoadedIndexes] = useState<number[]>([]);

	// Lazy load background images
	useEffect(() => {
		if (!images.length) return;

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const index = Number(entry.target.getAttribute('data-index'));
						if (!loadedIndexes.includes(index)) setLoadedIndexes(prev => [...prev, index]);

						observer.unobserve(entry.target);
					}
				});
			},
			{ scrollMargin: '400px 0px' } as any
		);

		slidesRef.current.forEach(slide => observer.observe(slide));
		return () => observer.disconnect();
	}, [images, loadedIndexes]);

	useEffect(() => {
		loadedIndexes.forEach(i => {
			const slide = slidesRef.current[i];
			if (slide)
				gsap.fromTo(slide, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.out' });
		});
	}, [loadedIndexes]);

	const currentIndexRef = useRef(0);
	useEffect(() => {
		if (!images.length) return;

		const interval = setInterval(() => {
			currentIndexRef.current = (currentIndexRef.current + 1) % images.length;
			slidesRef.current.forEach((slide, i) => {
				gsap.set(slide, {
					opacity: i === currentIndexRef.current ? 1 : 0,
					zIndex: i === currentIndexRef.current ? 1 : 0
				});
			});
		}, 3000);

		return () => clearInterval(interval);
	}, [images]);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const title = wrapper?.querySelector(`.${css.title}`);

		if (!wrapper || !title) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.set(title, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(title, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: title,
							start: 'top bottom',
							toggleActions: 'play none none reverse'
						}
					});

					itemsRef.current.forEach(el => {
						const titleEl = el.querySelector('h4');
						const textEl = el.querySelector('div');

						gsap.fromTo(
							titleEl,
							{ opacity: 0 },
							{
								opacity: 1,
								ease: 'none',
								scrollTrigger: {
									trigger: el,
									start: 'top bottom',
									end: 'bottom 60%',
									scrub: true
								}
							}
						);

						gsap.fromTo(
							textEl,
							{ opacity: 0, x: 100 },
							{
								opacity: 1,
								x: 0,
								ease: 'none',
								scrollTrigger: {
									trigger: el,
									start: 'top bottom',
									end: 'bottom 60%',
									scrub: true
								}
							}
						);
					});
				}
			);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	const addItemRef = (el: HTMLLIElement | null) => {
		if (el && !itemsRef.current.includes(el)) itemsRef.current.push(el);
	};

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<div className={css.slideshow}>
				{images.map((item, index) => (
					<div
						key={index}
						ref={el => {
							if (el) slidesRef.current[index] = el;
						}}
						data-index={index}
						className={css.slide}
						style={{
							backgroundImage: loadedIndexes.includes(index)
								? `url(${item.image?.url ?? getMedia(item.image)?.url})`
								: undefined
						}}
					/>
				))}
			</div>

			<div className={css.content}>
				<Typography uppercase className={css.title} variant="h2" tag="h2">
					{title || 'Yes, Q Can'}
				</Typography>

				<ul className={css.itemsList}>
					{isArrayNotEmpty(itemsList) &&
						itemsList.map(({ id, title, description }, idx) => (
							<li key={id} ref={addItemRef} className={css.listItem}>
								{device === 'mobile' && <span className={css.index}>0{idx + 1}</span>}

								<Typography uppercase className={css.itemTitle} variant="h2" tag="h3">
									<StrapiRichContent content={title} />
								</Typography>

								<Typography className={css.itemDescription} variant="body-l" tag="div">
									{device === 'desktop' && <span className={css.index}>0{idx + 1}</span>}
									<span>{description}</span>
								</Typography>
							</li>
						))}
				</ul>
			</div>
		</section>
	);
};

export default YesQCan;
